<template>
  <v-card flat :loading="isLoading" :min-width="minWidth">
    <slot name="header"></slot>
    <v-card-text style="min-height: 200px">
      <v-form ref="form" :disabled="readonly || isLoading">
        <v-container fluid>
          <v-row>
            <v-col cols="12" md="3">
              <div class="label mb-2">Class Date</div>
              <v-menu
                ref="classDateMenu"
                v-model="classDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.class_date"
                    :rules="[validationRules.required]"
                    placeholder="Select class date"
                    prepend-icon="mdi-calendar"
                    solo
                    flat
                    outlined
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.class_date"
                  @change="$refs.classDateMenu.save($event)"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="3">
              <div class="label mb-2">Class Time</div>
              <v-row align="center" dense>
                <v-col cols="auto">
                  <time-picker
                    v-model="form.start_time"
                    class="vue__time-picker--dense mb-6"
                    input-width="100px"
                    close-on-complete
                    manual-input
                  />
                </v-col>
                <span class="mb-6">-</span>
                <v-col cols="auto">
                  <time-picker
                    v-model="form.end_time"
                    class="vue__time-picker--dense mb-6"
                    input-width="100px"
                    close-on-complete
                    manual-input
                  />
                </v-col>
              </v-row>
            </v-col>
            <template v-if="!hideTerms">
              <v-col cols="12" md="2">
                <div class="label mb-2">Term</div>
                <v-text-field
                  v-model="form.term"
                  type="number"
                  :rules="[validationRules.required]"
                  :disabled="readonly || readonlyTerms"
                  solo
                  flat
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="2">
                <div class="label mb-2">Month</div>
                <v-text-field
                  v-model="form.month"
                  type="number"
                  :rules="[validationRules.required]"
                  :disabled="readonly || readonlyTerms"
                  solo
                  flat
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="2">
                <div class="label mb-2">Session</div>
                <v-text-field
                  v-model="form.session"
                  type="number"
                  :rules="[validationRules.required]"
                  :disabled="readonly || readonlyTerms"
                  solo
                  flat
                  outlined
                  dense
                />
              </v-col>
            </template>
          </v-row>
          <v-card v-if="!hideLessons" flat>
            <v-card-title>Study Materials</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-list flat>
                <template v-for="(lesson, i) in form.lessons">
                  <v-list-item :key="i">
                    <v-list-item-content>
                      <v-container fluid class="pa-0">
                        <v-row>
                          <v-col cols="12" md="3">
                            <div class="label mb-2">Lesson</div>
                            <v-select
                              v-model="form.lessons[i]"
                              :items="filteredLessons"
                              return-object
                              :item-text="
                                (item) => bilingualText(item, 'name', 'name_cn')
                              "
                              :disabled="readonly || readonlyLessons"
                              solo
                              flat
                              outlined
                              dense
                              placeholder="Select lesson"
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <div class="label mb-2">Category</div>
                            {{ getCategoryName(lesson.lesson_category_id) }}
                          </v-col>
                          <v-col cols="12" md="3">
                            <div class="label mb-2">Material File(s)</div>
                            <div v-if="lesson.attachment_file_url == null">
                              -
                            </div>
                            <ul v-else>
                              <li
                                v-for="(
                                  attachment_url, j
                                ) in lesson.attachment_file_url.split(',')"
                                :key="j"
                              >
                                <a :href="attachment_url" target="_blank">
                                  {{ filenameFromUrl(attachment_url) }}
                                </a>
                              </li>
                            </ul>
                          </v-col>
                          <v-col cols="12" md="3" class="text-right">
                            <v-btn
                              v-if="!readonly || !readonlyLessons"
                              depressed
                              color="error"
                              @click="removeLesson(i)"
                            >
                              <v-icon left>mdi-delete-forever</v-icon> Delete
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider
                    v-if="i < form.lessons.length - 1"
                    :key="`div-${i}`"
                  ></v-divider>
                </template>
                <v-list-item v-if="!readonly || !readonlyLessons">
                  <v-btn depressed color="success" @click="addLesson">
                    <v-icon left>mdi-plus-circle</v-icon> Add Lesson
                  </v-btn>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col v-if="!hideCancel" md="3" cols="12">
            <v-btn block depressed outlined @click="$emit('cancel', true)">
              Cancel
            </v-btn>
          </v-col>
          <v-col v-if="!hideViewDetails" md="3" cols="12">
            <v-btn
              block
              depressed
              outlined
              :href="
                hasPermission('classroom-schedules.update')
                  ? $route('classroom-schedules.update', item.id)
                  : $route('classroom-schedules.detail', item.id)
              "
            >
              View Details
            </v-btn>
          </v-col>
          <v-col v-if="!readonly" md="3" cols="12">
            <v-btn block depressed color="primary" @click="submit">
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'EditScheduleForm',
  props: {
    item: {
      type: Object,
      required: true,
    },
    minWidth: {
      type: [String, Number],
      default: null,
      required: false,
    },
    lessonPlan: {
      type: Object,
      default: () => null,
      required: false,
    },
    hideLessons: {
      type: Boolean,
      default: false,
      required: false,
    },
    readonlyLessons: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideTerms: {
      type: Boolean,
      default: false,
      required: false,
    },
    readonlyTerms: {
      type: Boolean,
      default: false,
      required: false,
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false,
    },
    submitApi: {
      type: Boolean,
      default: false,
    },
    hideCancel: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideViewDetails: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      classDateMenu: false,
      form: {
        class_date: null,
        start_time: '',
        end_time: '',
        term: null,
        month: null,
        session: null,
        lessons: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'classroomSchedule/isDetailLoading',
      lessonList: 'lesson/list',
      isLessonListLoading: 'lesson/isListLoading',
      categoryList: 'lessonCategory/list',
      isCategoryListLoading: 'lessonCategory/isListLoading',
    }),
    lessons() {
      return this.lessonPlan ? this.lessonList.data : this.item.lessons
    },
    categories() {
      return this.categoryList.data
    },
    filteredLessons() {
      if (this.lessons.length === 0) {
        return []
      }
      return this.lessons.filter((e) => {
        let termValid = this.form.term ? this.form.term == e.term : true
        let monthValid = this.form.month ? this.form.month == e.month : true
        let sessionValid = this.form.session
          ? this.form.session == e.session
          : true
        return termValid && monthValid && sessionValid
      })
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      updateSchedule: 'classroomSchedule/update',
      getLessonList: 'lesson/getList',
      getCategoryList: 'lessonCategory/getList',
      getSubscriptionPackage: 'subscriptionPackage/getDetail',
      handleError: 'handleError',
    }),
    async init() {
      _.merge(this.form, this.item)
      await this.getCategoryList()
      if (this.lessonPlan) {
        await this.getLessonList({
          lesson_plan_id: this.lessonPlan.id,
        })
      }
    },
    resetForm() {
      this.form = {
        class_date: null,
        start_time: '',
        end_time: '',
        term: null,
        month: null,
        session: null,
        lessons: [],
      }
    },
    removeLesson(index) {
      this.form.lessons.splice(index, 1)
    },
    addLesson() {
      this.form.lessons.push({
        id: null,
        name: null,
        name_cn: null,
        lesson_category_id: null,
        attachment_file_url: null,
      })
    },
    getCategoryName(categoryId) {
      const category = this.categories.find((e) => e.id === categoryId)
      return category ? this.bilingualText(category, 'name', 'name_cn') : ''
    },
    cancel() {
      this.$emit('cancel', true)
    },
    async submit() {
      if (this.$refs.form.validate()) {
        let err = null
        if (this.submitApi) {
          err = await this.updateSchedule(this.form)
        }
        if (!err) {
          this.$emit('submit', this.form)
          this.$emit('update:item', this.form)
        }
      }
    },
  },
}
</script>
